import { MarkdownText } from '@onefootprint/ui';
import { Children, cloneElement, type HTMLAttributes, isValidElement, type ReactNode, useEffect, useRef } from 'react';
import { type Components, defaultUrlTransform } from 'react-markdown';
import { childrenToText, ENTITY_ID_EXACT_RE, FP_ENTITY_SCHEME, linkifyFpIds } from '../../utils/fp-id-markdown';
import { linkifySources, SOURCE_SCHEME } from '../../utils/source-markdown';
import IdBadge from '../id-badge';
import PageLink from '../page-link';
import SourceChip from '../source-chip';
const isDashboardHref = (href?: string) => !!href?.startsWith('/') && !href.startsWith('//');

// A paragraph that holds nothing but a dashboard link is that link on its own line.
const standaloneDashboardLink = (children: ReactNode) => {
  const parts = Children.toArray(children).filter(part => typeof part !== 'string' || part.trim());
  const [only] = parts;
  return parts.length === 1 && isValidElement<{
    href?: string;
    standalone?: boolean;
  }>(only) && isDashboardHref(only.props.href) ? only : undefined;
};

// Preserve our custom schemes (react-markdown's sanitizer would otherwise drop them).
const fpEntityUrlTransform = (url: string) => url.startsWith(FP_ENTITY_SCHEME) || url.startsWith(SOURCE_SCHEME) ? url : defaultUrlTransform(url);

// Minimal hast shape; avoids depending on the un-hoisted `@types/hast`.
type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: Record<string, unknown>;
  children?: HastNode[];
};

// Monospace reveals as one unit, not word-by-word: splitting it would break
// whitespace + the `code` entity-id check below. `pre` is block, so it needs a
// block wrapper rather than an inline span.
const UNIT_TAGS = new Set(['code', 'pre']);

// Stagger new words so a multi-word update reveals one at a time; big bursts
// compress to fit the window
const STAGGER_STEP_MS = 20;
const STAGGER_WINDOW_MS = 400;

// Wrap each word (and each code/pre element, as a single unit) in a fade-in span,
// staggering those new this render (index >= prevCount). Whitespace stays bare text
// so wrapping is unaffected. Returns the total unit count (next render's prevCount).
// Only newly-mounted spans animate: react-markdown's stable per-tag keys keep prior
// units' DOM nodes.
const staggerWalk = (root: HastNode, prevCount: number): number => {
  let index = 0;
  const fresh: HastNode[] = [];

  // Wrap one unit (a word, or a whole code/pre element) so it takes a single slot
  // in the cascade. `pre` gets a block wrapper to avoid nesting a block in a span.
  const wrap = (inner: HastNode, block: boolean): HastNode => {
    const el: HastNode = {
      type: 'element',
      tagName: block ? 'div' : 'span',
      properties: {
        className: ['stream-word-in']
      },
      children: [inner]
    };
    if (index >= prevCount) fresh.push(el);
    index += 1;
    return el;
  };
  const visit = (node: HastNode): void => {
    if (!node.children) return;
    const next: HastNode[] = [];
    for (const child of node.children) {
      if (child.type === 'text') {
        for (const token of (child.value ?? '').split(/(\s+)/)) {
          if (token === '') continue;
          if (/^\s+$/.test(token)) next.push({
            type: 'text',
            value: token
          });else next.push(wrap({
            type: 'text',
            value: token
          }, false));
        }
        continue;
      }
      // Reveal a code/pre element as one unit; don't recurse into its text.
      if (child.type === 'element' && child.tagName && UNIT_TAGS.has(child.tagName)) {
        next.push(wrap(child, child.tagName === 'pre'));
        continue;
      }
      visit(child);
      next.push(child);
    }
    node.children = next;
  };
  visit(root);
  const step = Math.min(STAGGER_STEP_MS, STAGGER_WINDOW_MS / Math.max(fresh.length, 1));
  fresh.forEach((el, k) => {
    // `both` fill-mode keeps it hidden through its delay (see `.stream-word-in`).
    if (el.properties) el.properties.style = `animation-delay:${Math.round(k * step)}ms`;
  });
  return index;
};

// rehype plugin: stagger words new since `prevCount`, report the total via
// `countRef` (idempotent write → StrictMode-safe).
const createStaggerPlugin = (prevCount: number, countRef: {
  current: number;
}) => () => (tree: HastNode) => {
  countRef.current = staggerWalk(tree, prevCount);
};

// Chat-scale overrides for MarkdownText's page-scale defaults.
//
// Rhythm rationale (matched against ChatGPT/Otter/Sana/Cohere refs from Mobbin):
//
// - Headings get ~2x more space ABOVE than BELOW so a new section reads as a clean
//   break. MarkdownText's wrapper applies an 8px top margin to every element that
//   directly follows a heading (h1..h6); we override that by setting an explicit
//   bottom margin on the heading itself, using the important modifier so it wins
//   on specificity ties.
// - Heading SIZES barely jump from body (about +1-2px) and lean on WEIGHT, not
//   size, for hierarchy. h2 = heading-5 (16/700), h3 = label-1 (15/525), h4+
//   scale down.
// - Lists drop MarkdownText's space-y-2 plus li.mb-2 doubling — single-spaced
//   bullets at about 6-8px between, a la ChatGPT.
// - first:!mt-0 keeps a heading flush to the top of a message (no orphan gap
//   above the first child).
const components: Components = {
  // Section opener — small step up from body-2 (16/700 vs 14/400). Used sparingly:
  // the agent rarely emits a real h1 inside a chat turn.
  h1: ({
    children
  }: {
    children?: ReactNode;
  }) => <h1 className="text-heading-5 text-primary !mt-8 !mb-2 first:!mt-0">{children}</h1>,
  // Most common section heading — still 16px to match h1 weight but kept on the
  // same line as h1 typographically so multi-level docs don't pop visually.
  h2: ({
    children
  }: {
    children?: ReactNode;
  }) => <h2 className="text-heading-5 text-primary !mt-6 !mb-2 first:!mt-0">{children}</h2>,
  // Subsection — same SIZE as body (15px), just weight-bumped. Matches the
  // ChatGPT/Otter "bold lead-in" pattern: hierarchy from weight, not size.
  h3: ({
    children
  }: {
    children?: ReactNode;
  }) => <h3 className="text-label-1 text-primary !mt-5 !mb-1.5 first:!mt-0">{children}</h3>,
  // h4/h5/h6 — clamp to body-scale weights so the cascade flattens at the chat
  // level. Without this, MarkdownText falls back to text-heading-4 (17/700),
  // which towers over the 14px chat body.
  h4: ({
    children
  }: {
    children?: ReactNode;
  }) => <h4 className="text-label-2 text-primary !mt-4 !mb-1 first:!mt-0">{children}</h4>,
  h5: ({
    children
  }: {
    children?: ReactNode;
  }) => <h5 className="text-label-3 text-primary !mt-4 !mb-1 first:!mt-0">{children}</h5>,
  h6: ({
    children
  }: {
    children?: ReactNode;
  }) => <h6 className="text-label-3 text-secondary !mt-4 !mb-1 first:!mt-0">{children}</h6>,
  // Single-spaced bullets, ~6px between rows (`space-y-1.5` = 6px). Overrides
  // MarkdownText's `space-y-2 + li.mb-2` doubling.
  ul: ({
    children
  }: {
    children?: ReactNode;
  }) => <ul className="list-disc list-outside pl-5 space-y-1.5 marker:text-tertiary">{children}</ul>,
  ol: ({
    children
  }: {
    children?: ReactNode;
  }) => <ol className="list-decimal list-outside pl-5 space-y-1.5 marker:text-tertiary">{children}</ol>,
  li: ({
    children
  }: {
    children?: ReactNode;
  }) => <li className="!mb-0 pl-0.5">{children}</li>,
  p: ({
    children,
    ...rest
  }: HTMLAttributes<HTMLParagraphElement> & {
    node?: unknown;
  }) => {
    const link = standaloneDashboardLink(children);
    const {
      node: _node,
      ...props
    } = rest;
    return <p {...props}>{link ? cloneElement(link, {
        standalone: true
      }) : children}</p>;
  },
  a: ({
    href,
    children,
    standalone
  }: {
    href?: string;
    children?: ReactNode;
    standalone?: boolean;
  }) => href?.startsWith(FP_ENTITY_SCHEME) ? <IdBadge id={href.slice(FP_ENTITY_SCHEME.length)} /> : href?.startsWith(SOURCE_SCHEME) ? <SourceChip>{children}</SourceChip> : href && isDashboardHref(href) ? <PageLink href={href} standalone={standalone}>
        {children}
      </PageLink> : <a href={href} target="_blank" rel="noopener noreferrer" className="underline">
        {children}
      </a>,
  code: ({
    children,
    ...rest
  }: HTMLAttributes<HTMLElement>) => {
    const text = childrenToText(children).trim();
    if (ENTITY_ID_EXACT_RE.test(text)) return <IdBadge id={text} />;
    // Mirror IdBadge's pill so any inline code (`fp_id`, `entity_fp_id`, …) reads
    // as part of the same family: mono, accent, subtle bordered chip.
    return <code {...rest} className="font-mono text-[13px] text-accent bg-secondary border border-tertiary rounded align-baseline px-1 py-0">
        {children}
      </code>;
  },
  // Code block: owns the surface; the `[&_code]` reset neutralizes the inline
  // pill styling above so the inner <code> doesn't get a nested background,
  // border, or accent color.
  pre: ({
    children
  }: {
    children?: ReactNode;
  }) => <pre className="font-mono text-caption-2 bg-secondary rounded-md p-3 my-3 overflow-x-auto whitespace-pre [&_code]:bg-transparent [&_code]:p-0 [&_code]:rounded-none [&_code]:border-0 [&_code]:text-secondary">
      {children}
    </pre>,
  blockquote: ({
    children
  }: {
    children?: ReactNode;
  }) => <blockquote className="border-l-2 border-tertiary pl-3 text-secondary my-3">{children}</blockquote>
};
type ChatMarkdownProps = {
  content: string;
  /** Fade newly-streamed words in. Off for completed/history messages. */
  animate?: boolean;
};
const ChatMarkdown = ({
  content,
  animate
}: ChatMarkdownProps) => {
  // Previous render's word count = the boundary for "new this render". The plugin
  // writes the live total to `wordCount`; the effect promotes it post-commit.
  const prevWordCount = useRef(0);
  const wordCount = useRef(0);
  useEffect(() => {
    prevWordCount.current = wordCount.current;
  });

  // Lock streaming so the final frame (which arrives with `animate` false) still
  // animates its tail. History never latches; resets on unmount → remount reads as history.
  const everStreamed = useRef(false);
  if (animate) everStreamed.current = true;
  const stagger = animate || everStreamed.current;
  return (
    // Chat-density sibling rhythm. `MarkdownText` ships its own page-scale rules
    // at the same specificity (single class + adjacent sibling), so we use `!`
    // modifiers — relying on Tailwind's emit order between the two `mt-*` rules
    // is undocumented and could silently regress on a future bump.
    //   - p+p: 12px (page default felt cramped at chat density)
    //   - p+ul / p+ol: 8px before a list following a paragraph
    //   - ul+p / ol+p: 12px when prose resumes after a list
    // `w-full + min-w-0` pin the markdown to the message column width; without
    // them a wide table (in a `flex items-start` column) grows the whole column
    // past the chat max-width and defeats the table's own overflow-x scroll.
    <div className="text-body-2 w-full min-w-0 [&_p+p]:!mt-3 [&_p+ul]:!mt-2 [&_p+ol]:!mt-2 [&_ul+p]:!mt-3 [&_ol+p]:!mt-3">
      <MarkdownText components={components} urlTransform={fpEntityUrlTransform} rehypePlugins={stagger ? [createStaggerPlugin(prevWordCount.current, wordCount)] : undefined}>
        {linkifySources(linkifyFpIds(content))}
      </MarkdownText>
    </div>
  );
};
export default ChatMarkdown;